const LANGUAGE_STORAGE_KEY = 'app-language';
const LANGUAGE_CHANGE_EVENT = 'languageChanged';

export type SupportedLanguage = 'es' | 'en';

/**
 * Language synchronization service
 * Keeps Host and MFE i18n instances in sync using localStorage and custom events
 */
export const LanguageSyncService = {
  /**
   * Get the current language from localStorage
   */
  getLanguage(): SupportedLanguage {
    if (typeof window === 'undefined') return 'es';
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return stored === 'es' || stored === 'en' ? stored : 'es';
  },

  /**
   * Set the language in localStorage and dispatch event
   */
  setLanguage(language: SupportedLanguage): void {
    if (typeof window === 'undefined') return;
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);

    // Dispatch custom event so both Host and MFE can listen
    window.dispatchEvent(
      new CustomEvent(LANGUAGE_CHANGE_EVENT, {
        detail: { language },
      }),
    );
  },

  /**
   * Listen for language changes from other contexts (Host or MFE)
   */
  onLanguageChange(
    callback: (_language: SupportedLanguage) => void,
  ): () => void {
    if (typeof window === 'undefined') return () => {};

    const handler = (event: Event) => {
      const customEvent = event as CustomEvent<{ language: SupportedLanguage }>;
      callback(customEvent.detail.language);
    };

    window.addEventListener(LANGUAGE_CHANGE_EVENT, handler);

    // Return cleanup function
    return () => {
      window.removeEventListener(LANGUAGE_CHANGE_EVENT, handler);
    };
  },

  /**
   * Initialize language from localStorage or browser
   */
  initLanguage(): SupportedLanguage {
    if (typeof window === 'undefined') return 'es';

    // Priority: localStorage > browser > default
    const stored = this.getLanguage();
    if (stored) return stored;

    const browserLang = navigator.language.split('-')[0];
    const detected =
      browserLang === 'es' || browserLang === 'en' ? browserLang : 'es';

    this.setLanguage(detected);
    return detected;
  },
};
